import '../css/ShowOrder.css'
import * as React from 'react';
import { useState, useEffect } from "react";
import CircularIndeterminate from '../components/CircularLoad';
import { Link, useParams } from 'react-router-dom';
import { responseArray } from '../utils/apiFetch';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export default function ShowOrder() {
    const { orderNb } = useParams();
    const [order, setOrder] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchOrder();
    }, []);

    const fetchOrder = async () => {
        try {
            const response = await fetch(`/api/order/${orderNb}`);
            if (response.ok) {
              const results = await response.json();
              setOrder(results.order)
            } else {
              const contentType = response.headers.get('content-type') || '';
              const data = contentType.includes('application/json') ? await response.json() : null;
              setError(data?.message || 'Unable to load this order.');
            }
          } catch (error) {
            console.error('Error:', error);
            setError('Unable to load this order.');
          }
    };

    if (order === null) {
        return (
            <div className="show-order-loading">
                {error ? <p>{error}</p> : <CircularIndeterminate display={true}/>}
            </div>
        )
    }

    const products = responseArray(order, 'products').filter(item => item?.product);
    const formatPrice = (value) => Number(value || 0).toFixed(2);
    const formatDate = (value) => {
        if (!value) return 'Unknown date';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return 'Unknown date';
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };
    const quantityOf = (item) => Number(item.quantity || item.nb_ordered || 1);
    const orderTotal = products.reduce((sum, item) => {
        return sum + Number(item.product.price || 0) * quantityOf(item);
    }, 0);
    const address = order.shipping_address || {};
    const addressLine = [address.street, address.building && `Building ${address.building}`, address.floor && `Floor ${address.floor}`]
        .filter(Boolean)
        .join(', ');
    const cityLine = [address.city, address.state].filter(Boolean).join(', ');

    return (
        <main className="show-order-page">
            <Link to="/user/order-history" className="show-order-back">
                <ArrowBackIcon fontSize="small" />
                Order history
            </Link>

            <section className="show-order-hero">
                <div className="show-order-title">
                    <span className="show-order-icon">
                        <ReceiptLongIcon />
                    </span>
                    <div>
                        <span className="show-order-label">Order details</span>
                        <h1>Order #{order.number}</h1>
                    </div>
                </div>
                <span className="show-order-status">{order.status || 'Pending'}</span>
            </section>

            <section className="show-order-summary">
                <article>
                    <CalendarTodayIcon />
                    <div>
                        <span>Date ordered</span>
                        <strong>{formatDate(order.date)}</strong>
                    </div>
                </article>
                <article>
                    <LocalShippingIcon />
                    <div>
                        <span>Items</span>
                        <strong>{products.length} {products.length === 1 ? 'product' : 'products'}</strong>
                    </div>
                </article>
                <article>
                    <ReceiptLongIcon />
                    <div>
                        <span>Estimated total</span>
                        <strong>${formatPrice(orderTotal)}</strong>
                    </div>
                </article>
            </section>

            <section className="show-order-content">
                <div className="show-order-products">
                    <div className="show-order-section-title">
                        <h2>Products</h2>
                        <span>{products.length} {products.length === 1 ? 'item' : 'items'}</span>
                    </div>

                    {products.length === 0 ? (
                        <p className="show-order-empty-products">
                            No products are attached to this order. This can happen for orders placed before order product storage was fixed.
                        </p>
                    ) : products.map((item) => {
                        const product = item.product;
                        const quantity = quantityOf(item);
                        const imageUrl = product.images?.[0]?.url;

                        return (
                            <article className="show-order-product" id={`product-${product._id}`} key={`product-${product._id}`}>
                                <Link to={`/product/${product._id}`} className="show-order-product-img">
                                    {imageUrl ? (
                                        <img alt={product.english_name} src={imageUrl} />
                                    ) : (
                                        <ReceiptLongIcon />
                                    )}
                                </Link>
                                <div className="show-order-product-info">
                                    <Link to={`/product/${product._id}`}>{product.english_name}</Link>
                                    <span>{product.brand}</span>
                                </div>
                                <div className="show-order-product-numbers">
                                    <span>Qty {quantity}</span>
                                    <strong>${formatPrice(Number(product.price || 0) * quantity)}</strong>
                                </div>
                            </article>
                        );
                    })}
                </div>

                <aside className="show-order-side">
                    <div className="show-order-section-title">
                        <h2>Shipping</h2>
                    </div>
                    <div className="show-order-address">
                        <LocationOnIcon />
                        <div>
                            <strong>{addressLine || 'Shipping address'}</strong>
                            {cityLine && <span>{cityLine}</span>}
                            {address.extra_info && <span>{address.extra_info}</span>}
                            {address.phone_nb && <span>Phone: {address.phone_nb}</span>}
                        </div>
                    </div>
                </aside>
            </section>
        </main>
    )
}
